"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import {
  approveTronUsdtViaMetaMask,
  disconnectMetaMaskTron,
  getReadonlyTronWeb,
  isMetaMaskTronAvailable,
  isMetaMaskTronSelected,
  readMetaMaskTronAddress,
  connectMetaMaskTron,
} from "../wallet/metamask-tron"
import {
  getTronProviderWindow,
  hasTronInjection,
  readTronAddress,
  requestTronAccounts,
} from "../wallet/tron-provider"

export function useTronWallet() {
  const [address, setAddress] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [hasTronLink, setHasTronLink] = useState(false)
  const [hasMetaMaskTron, setHasMetaMaskTron] = useState(false)

  const syncFromProvider = useCallback(() => {
    const base58 = readTronAddress() ?? readMetaMaskTronAddress()
    setAddress((prev) => (prev === base58 ? prev : base58))
    setIsConnected((prev) => {
      const next = Boolean(base58)
      return prev === next ? prev : next
    })
    return Boolean(base58)
  }, [])

  useEffect(() => {
    setHasTronLink(hasTronInjection())
    void isMetaMaskTronAvailable().then(setHasMetaMaskTron)
    syncFromProvider()
    const timer = window.setInterval(() => {
      setHasTronLink(hasTronInjection())
      syncFromProvider()
    }, 1500)
    return () => window.clearInterval(timer)
  }, [syncFromProvider])

  const connect = useCallback(async () => {
    if (isMetaMaskTronSelected()) {
      await connectMetaMaskTron()
    } else {
      await requestTronAccounts()
    }
    if (!syncFromProvider()) {
      throw new Error("Tron wallet connection failed")
    }
  }, [syncFromProvider])

  const disconnect = useCallback(() => {
    if (isMetaMaskTronSelected()) {
      void disconnectMetaMaskTron()
    }
    setAddress(null)
    setIsConnected(false)
  }, [])

  const canUseTron = hasTronLink || hasMetaMaskTron || isMetaMaskTronSelected()

  return useMemo(
    () => ({
      address,
      isConnected,
      connect,
      disconnect,
      syncFromProvider,
      /** True when TRON is available via injection (TronLink, Trust, etc.) or MetaMask. */
      hasTronLink: canUseTron,
    }),
    [address, canUseTron, connect, disconnect, isConnected, syncFromProvider],
  )
}

export async function readTronAllowance(
  ownerBase58: string,
  tokenAddress: string,
  spenderBase58: string,
): Promise<bigint> {
  const injected = getTronProviderWindow()?.tronWeb
  const tronWeb =
    injected?.ready === true ? injected : getReadonlyTronWeb()
  const contract = await tronWeb.contract().at(tokenAddress)
  const ownerHex = tronWeb.address.toHex(ownerBase58)
  const spenderHex = tronWeb.address.toHex(spenderBase58)
  const value = await contract.allowance(ownerHex, spenderHex).call()
  const raw = typeof value === "string" ? value : value.toString()
  return BigInt(raw)
}

export async function approveTronUsdt(
  tokenAddress: string,
  spenderBase58: string,
): Promise<string> {
  const owner = readTronAddress() ?? readMetaMaskTronAddress()
  if (!owner) {
    throw new Error("Tron wallet not connected")
  }

  if (isMetaMaskTronSelected() && readMetaMaskTronAddress()) {
    return approveTronUsdtViaMetaMask(tokenAddress, spenderBase58, owner)
  }

  const tronWeb = getTronProviderWindow()?.tronWeb
  if (!tronWeb?.ready) {
    throw new Error("TronWeb not ready")
  }
  const contract = await tronWeb.contract().at(tokenAddress)
  const spenderHex = tronWeb.address.toHex(spenderBase58)
  const max =
    "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff"
  const txId = await contract.approve(spenderHex, max).send()
  if (typeof txId === "string" && txId.trim()) {
    return txId.trim()
  }
  if (txId && typeof txId === "object") {
    const obj = txId as Record<string, unknown>
    for (const key of ["txid", "txId", "txID", "transaction", "id"]) {
      const value = obj[key]
      if (typeof value === "string" && value.trim()) {
        return value.trim()
      }
    }
  }
  // Some wallets resolve approve() without a usable id — caller may omit hash.
  return typeof txId === "string" ? txId : ""
}
